import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Clock, Timer, Sparkles, Wifi, ArrowLeft, User, LogOut } from 'lucide-react';
import { useRealtimeBroadcast } from '../hooks/useRealtimeBroadcast';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import UserProfileModal from './UserProfileModal';

const BroadcastView: React.FC = () => {
  const { roomId, linkId } = useParams();
  const { room, loading, error } = useRealtimeBroadcast(roomId!, linkId!);
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const navigate = useNavigate();
  const [showUserProfile, setShowUserProfile] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return '☀️';
      case 'dark':
        return '🌙';
      case 'system':
        return '💻';
    }
  };

  const cycleTheme = () => {
    const themes: Array<'light' | 'dark' | 'system'> = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 px-4 transition-all duration-300">
        <div className="text-center">
          <div className="relative mb-4 sm:mb-6">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 mx-auto"></div>
            <div className="absolute inset-0 rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-transparent border-t-purple-400 dark:border-t-purple-600 animate-spin mx-auto" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          </div>
          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg font-medium">Loading room...</p>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-2">Connecting to live status</p>
        </div>
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-red-50 to-orange-100 dark:from-slate-900 dark:via-red-900/20 dark:to-orange-900/20 p-4 sm:p-6 transition-all duration-300">
        <div className="text-center max-w-md">
          <div className="relative mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/30 dark:to-orange-900/30 rounded-2xl sm:rounded-3xl mx-auto flex items-center justify-center shadow-lg">
              <AlertCircle className="w-8 h-8 sm:w-10 sm:h-10 text-red-500" />
            </div>
            <div className="absolute -top-2 -right-2 w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white text-xs sm:text-sm font-bold">!</span>
            </div>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-200 mb-2 sm:mb-3">
            {error === 'This broadcast link has been deactivated' 
              ? 'Link Deactivated' 
              : error === 'This broadcast link has expired'
              ? 'Link Expired'
              : 'Invalid or Expired Link'
            }
          </h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm sm:text-base">
            {error === 'This broadcast link has been deactivated'
              ? 'This broadcast link has been deactivated by the room administrator.'
              : error === 'This broadcast link has expired'
              ? 'This broadcast link has expired and is no longer active.'
              : 'This broadcast link is no longer active or does not exist.'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-all duration-300">
      {/* Header with navigation and user options */}
      <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl shadow-lg border-b border-white/20 dark:border-slate-700/50 sticky top-0 z-40">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center">
            {/* Back to RoomState button */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Back to RoomState</span>
            </button>

            {/* User options */}
            <div className="flex items-center gap-2">
              <button
                onClick={cycleTheme}
                className="p-2 bg-white/70 dark:bg-slate-700/70 hover:bg-white/90 dark:hover:bg-slate-700/90 rounded-xl transition-colors shadow-md"
                title={`Theme: ${theme}`}
              >
                <span className="text-lg">{getThemeIcon()}</span>
              </button>
              
              {user && (
                <>
                  <button
                    onClick={() => setShowUserProfile(true)}
                    className="p-2 bg-white/70 dark:bg-slate-700/70 hover:bg-white/90 dark:hover:bg-slate-700/90 rounded-xl transition-colors shadow-md"
                    title="User Profile"
                  >
                    <User className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </button>
                  <button
                    onClick={handleLogout}
                    className="p-2 bg-white/70 dark:bg-slate-700/70 hover:bg-white/90 dark:hover:bg-slate-700/90 rounded-xl transition-colors shadow-md"
                    title="Logout"
                  >
                    <LogOut className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-lg">
          {/* Live indicator */}
          <div className="flex items-center justify-center gap-2 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-full border border-green-200 dark:border-green-700">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <Wifi className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm font-medium">Live Status</span>
            </div>
          </div>

          <div 
            className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-xl border-2 transition-all duration-300 hover:shadow-2xl overflow-hidden"
            style={{ borderColor: room.currentState.color }}
          >
            {/* Status indicator bar */}
            <div 
              className="h-1.5 sm:h-2 w-full transition-all duration-500"
              style={{ backgroundColor: room.currentState.color }}
            />
            
            <div className="p-6 sm:p-8 relative">
              <div className="mb-6 sm:mb-8 text-center">
                <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full animate-pulse" style={{ backgroundColor: room.currentState.color }} />
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-200 truncate">{room.name}</h1>
                  <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full animate-pulse" style={{ backgroundColor: room.currentState.color }} />
                </div>
                <p className="text-slate-500 dark:text-slate-400 font-medium text-sm sm:text-base">Current Status</p>
              </div>

              <div className="flex items-center justify-center p-6 sm:p-8 mb-4 sm:mb-6">
                <div
                  className="flex items-center gap-3 text-2xl sm:text-3xl font-bold rounded-xl sm:rounded-2xl px-6 sm:px-8 py-3 sm:py-4 text-white transition-all duration-500 shadow-lg hover:shadow-xl transform hover:scale-105 text-center"
                  style={{ backgroundColor: room.currentState.color }}
                >
                  {room.currentState.emoji && (
                    <span className="text-3xl sm:text-4xl">{room.currentState.emoji}</span>
                  )}
                  <span>{room.currentState.name}</span>
                </div>
              </div>

              {room.viewMode === 'detailed' && (
                <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-slate-600 dark:text-slate-400 bg-slate-50/80 dark:bg-slate-700/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-center">Last updated: {new Date(room.lastUpdated).toLocaleString()}</span>
                  </div>
                  
                  {room.autoResetState && (
                    <div className="flex items-center justify-center gap-2">
                      <Timer className="w-3 h-3 sm:w-4 sm:h-4 text-orange-400 flex-shrink-0" />
                      <span className="text-center">Auto-reset after {room.autoResetState.afterMinutes} minutes</span>
                    </div>
                  )}
                </div>
              )}

              {/* Decorative elements */}
              <div className="absolute top-4 right-4 opacity-20">
                <Sparkles className="w-4 h-4 sm:w-6 sm:h-6 text-slate-400 dark:text-slate-500" />
              </div>
              <div className="absolute bottom-4 left-4 opacity-20">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400 dark:text-slate-500" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-4 sm:mt-6">
            <p className="text-slate-400 dark:text-slate-500 text-xs sm:text-sm">
              Powered by <span className="font-semibold text-slate-600 dark:text-slate-400">RoomState</span>
            </p>
          </div>
        </div>
      </div>

      <UserProfileModal
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
      />
    </div>
  );
};

export default BroadcastView;